import type { RequestScope } from "@/types/request-scope";
import type {
  TelemetryEvent,
  TelemetryScope,
  TelemetrySend,
} from "@/services/telemetry/contracts";
import type { TelemetryPage } from "./page-catalog";

export const SESSION_IDLE_MS = 30 * 60_000;
const ENGAGEMENT_IDLE_MS = 60_000;
export interface UsageSession {
  id: string;
  lastActivity: number;
}
interface Dependencies {
  now: () => number;
  id: () => string;
  session: (
    scope: TelemetryScope,
    now: number,
  ) => { value: UsageSession; created: boolean };
  touch: (scope: TelemetryScope, session: UsageSession) => void;
  send: TelemetrySend;
  environment: Pick<
    TelemetryEvent,
    "device" | "displayMode" | "navigationType" | "appVersion"
  >;
}
type Queued = { event: TelemetryEvent; attempts: number };

/** A bounded, non-durable queue. No credentials, user content, or events are written to browser storage. */
export class TelemetryCollector {
  private scope: TelemetryScope | null = null;
  private page: TelemetryPage | null = null;
  private usage: UsageSession | null = null;
  private queue: Queued[] = [];
  private controller = new AbortController();
  private sending = false;
  private visible = true;
  private lastInteraction = 0;
  private lastTick = 0;
  private lastPageKey = "";
  private openedPerson: string | null = null;
  private lastTouch = 0;
  private pendingActive = 0;
  private viewedPageKey = "";
  constructor(private readonly deps: Dependencies) {}

  /** A replacement identity/school discards old queued work; late responses cannot requeue it under new credentials. */
  setScope(scope: TelemetryScope | null) {
    if (
      this.scope?.generation === scope?.generation &&
      this.scope?.personId === scope?.personId &&
      this.scope?.tenantId === scope?.tenantId
    )
      return;
    this.controller.abort();
    this.controller = new AbortController();
    this.sending = false;
    this.queue = [];
    this.page = null;
    this.usage = null;
    this.lastPageKey = "";
    this.viewedPageKey = "";
    this.pendingActive = 0;
    this.lastTouch = 0;
    if (!scope || this.scope?.personId !== scope.personId)
      this.openedPerson = null;
    this.scope = scope;
    this.lastTick = this.deps.now();
    this.lastInteraction = this.lastTick;
  }

  /** React effect replay, filters and unrelated query changes cannot multiply an unchanged page impression. */
  visit(page: TelemetryPage | null) {
    const key = page ? `${page.page}:${page.guide ?? ""}` : "";
    if (key === this.lastPageKey) return;
    this.tick();
    this.page = page;
    this.lastPageKey = key;
    if (!page || !this.scope || !this.visible) return;
    this.ensureSession();
    this.lastInteraction = this.deps.now();
    this.touchSession();
    if (
      this.openedPerson !== this.scope.personId &&
      !page.page.endsWith(".join")
    ) {
      this.record("app_open");
      this.openedPerson = this.scope.personId;
    }
    this.record("page_view");
  }

  /** Only interaction timing is observed; event targets, input values and typed keys are never read. */
  activity() {
    if (!this.scope || !this.page || !this.visible) return;
    const now = this.deps.now();
    this.measureActivity();
    const newSession = this.ensureSession();
    this.lastInteraction = now;
    this.touchSession();
    if (newSession) this.record("page_view");
  }
  setVisible(visible: boolean) {
    this.tick();
    const resumed = visible && !this.visible;
    this.visible = visible;
    this.lastTick = this.deps.now();
    if (resumed && this.scope && this.page) {
      const newSession = this.ensureSession();
      this.lastInteraction = this.deps.now();
      this.touchSession();
      if (
        this.openedPerson !== this.scope.personId &&
        !this.page.page.endsWith(".join")
      ) {
        this.record("app_open");
        this.openedPerson = this.scope.personId;
      }
      if (newSession || this.viewedPageKey !== this.lastPageKey)
        this.record("page_view");
    }
  }

  /** Bounded visible, recently-interacted intervals provide estimates rather than wall-clock tab-open time. */
  tick() {
    this.measureActivity();
    if (this.pendingActive > 0)
      this.record(
        "active_time",
        undefined,
        Math.min(60_000, Math.round(this.pendingActive)),
      );
    this.pendingActive = 0;
  }
  /** Accumulate timing locally rather than emitting an analytics event for every keystroke or scroll. */
  private measureActivity() {
    const now = this.deps.now();
    if (this.scope && this.page && this.usage && this.visible) {
      this.pendingActive += Math.min(
        30_000,
        Math.max(
          0,
          Math.min(now, this.lastInteraction + ENGAGEMENT_IDLE_MS) -
            this.lastTick,
        ),
      );
    }
    this.lastTick = now;
  }
  action(scope: RequestScope | null, action: string, page?: TelemetryPage) {
    if (
      !scope ||
      !this.scope ||
      scope.generation !== this.scope.generation ||
      scope.personId !== this.scope.personId ||
      scope.tenantId !== this.scope.tenantId ||
      !this.page
    )
      return;
    this.activity();
    this.record("feature_action", action, 0, page);
  }

  /** At most 20 events per request, 100 in memory and three attempts per event; analytics never gates business UI. */
  async flush(keepalive = false) {
    if (this.sending || !this.scope || !this.queue.length) return;
    const scope = this.scope,
      controller = this.controller;
    const batch = this.queue.splice(0, 20);
    this.sending = true;
    try {
      const result = await this.deps.send(
        { schemaVersion: 1, events: batch.map((row) => row.event) },
        scope,
        keepalive,
        controller.signal,
      );
      if (this.controller === controller && result === "retry") {
        const retry = batch
          .map((row) => ({ ...row, attempts: row.attempts + 1 }))
          .filter((row) => row.attempts < 3);
        this.queue = [...retry, ...this.queue].slice(-100);
      }
    } catch {
      // Even an unexpected transport exception must not escape into a feature action or route effect.
      if (this.controller === controller)
        this.queue = [
          ...batch
            .map((row) => ({ ...row, attempts: row.attempts + 1 }))
            .filter((row) => row.attempts < 3),
          ...this.queue,
        ].slice(-100);
    } finally {
      if (this.controller === controller) this.sending = false;
    }
  }
  dispose() {
    this.controller.abort();
    this.scope = null;
    this.queue = [];
  }
  private ensureSession() {
    if (!this.scope) return false;
    const now = this.deps.now();
    const resolved = this.deps.session(this.scope, now);
    const changed = resolved.value.id !== this.usage?.id;
    if (changed && this.usage) {
      // Flush timing under the old session before replacing its identity.
      if (this.pendingActive > 0)
        this.record(
          "active_time",
          undefined,
          Math.min(60_000, Math.round(this.pendingActive)),
        );
      this.pendingActive = 0;
    }
    this.usage = resolved.value;
    if (resolved.created) this.record("session_started");
    return changed;
  }
  private touchSession() {
    if (!this.scope || !this.usage) return;
    const now = this.deps.now();
    if (now - this.lastTouch < 1000) return;
    this.lastTouch = now;
    this.usage = { ...this.usage, lastActivity: now };
    this.deps.touch(this.scope, this.usage);
  }
  private record(
    eventName: TelemetryEvent["eventName"],
    action?: string,
    activeMilliseconds = 0,
    page = this.page,
  ) {
    if (!this.scope || !this.usage || !page) return;
    const event: TelemetryEvent = {
      eventId: this.deps.id(),
      sessionId: this.usage.id,
      eventName,
      page: page.page,
      occurredAt: new Date(this.deps.now()).toISOString(),
      activeMilliseconds,
      ...this.deps.environment,
      ...(action ? { action } : {}),
      ...(page.guide ? { guide: page.guide } : {}),
    };
    if (eventName === "page_view")
      this.viewedPageKey = `${page.page}:${page.guide ?? ""}`;
    this.queue.push({ event, attempts: 0 });
    if (this.queue.length > 100) this.queue.shift();
  }
}
